import type { FieldNamesMarkedBoolean } from "react-hook-form";

import type {
  FsvListingUpdatePayload,
  FsvPricingErrorBody,
} from "../../../../../components/src/services/listings.service";

import {
  FSV_SAVE_ERRORS,
  ROUNDED_UP_FIELDS,
  SAVE_ERROR_MESSAGES,
} from "./ListingFsvForm.consts";
import type { ListingFsvFormValues } from "./ListingFsvForm.schema";
import type {
  FsvSaveError,
  RoundedUpAmount,
  RoundedUpField,
} from "./ListingFsvForm.types";

/**
 * Whether a number input's value is blank. The form types amounts as numbers,
 * but an input the host clears (or fills with something that isn't a number,
 * like "-") stores "", which TypeScript won't let us compare to a number.
 */
const isBlankAmount = (value: unknown) => value == null || value === "";

/** The FSV update payload, with only the fields the host changed. */
const buildFsvListingUpdatePayload = (
  values: ListingFsvFormValues,
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<ListingFsvFormValues>>>,
): FsvListingUpdatePayload => {
  const fullService: NonNullable<FsvListingUpdatePayload["full_service"]> = {};
  if (dirtyFields.roomType) fullService.room_type = values.roomType;
  if (dirtyFields.gratuityRate || dirtyFields.hasGratuity) {
    fullService.gratuity_percent = values.hasGratuity ? values.gratuityRate : 0;
  }
  if (dirtyFields.menuUrl && values.menuUrl) {
    fullService.menu_url = values.menuUrl;
  }

  const payload: FsvListingUpdatePayload = {};
  if (Object.keys(fullService).length > 0) payload.full_service = fullService;
  if (dirtyFields.foodAndDrinkMinimum) {
    payload.included_addon = { unit_cost: values.foodAndDrinkMinimum };
  }
  if (dirtyFields.spaceRental || dirtyFields.hasSpaceRental) {
    payload.flat_rate_price_value = values.hasSpaceRental
      ? values.spaceRental
      : 0;
  }

  return payload;
};

/**
 * The fractional amounts among `values`, each rounded up to a whole number.
 * Blank and whole amounts are left alone.
 */
const getRoundedUpAmounts = (
  values: Partial<Pick<ListingFsvFormValues, RoundedUpField>>,
): RoundedUpAmount[] =>
  ROUNDED_UP_FIELDS.flatMap((field) => {
    const value = values[field];
    if (isBlankAmount(value)) {
      return [];
    }

    const amount = Number(value);
    return Number.isFinite(amount) && !Number.isInteger(amount)
      ? [{ field, amount: Math.ceil(amount) }]
      : [];
  });

/**
 * Maps an FSV update or pricing-check error to what the form shows, per
 * `FSV_SAVE_ERRORS`. A minimum-spend error that comes without its amount
 * falls back to the general minimum-spend message.
 */
const getFsvSaveError = (
  error: FsvPricingErrorBody | undefined,
): FsvSaveError => {
  const code = error?.error_code;
  // Own keys only, so a code like "constructor" isn't found on the prototype
  if (!code || !Object.prototype.hasOwnProperty.call(FSV_SAVE_ERRORS, code)) {
    return { message: SAVE_ERROR_MESSAGES.submitError };
  }

  const { amountKey, field, message } = FSV_SAVE_ERRORS[code];
  if (!amountKey) {
    return { field, message };
  }

  const amount = error[amountKey];
  return amount != null
    ? { field, message, amount }
    : { field, message: SAVE_ERROR_MESSAGES.belowMinimumSpend };
};

export {
  buildFsvListingUpdatePayload,
  getFsvSaveError,
  getRoundedUpAmounts,
  isBlankAmount,
};
